import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { Landmark } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

type Region = "england" | "scotland" | "wales" | "us"
type PropertyType = "main" | "btl" | "company"
type USState = "CA" | "NY" | "FL" | "TX" | "IL" | "other"

interface Band { label: string; cap: number; rate: number }
interface BandResult { label: string; effectiveRate: number; taxable: number; tax: number }

const SDLT_STANDARD: Band[] = [
  { label: "£0 – £250,000", cap: 250000, rate: 0 },
  { label: "£250,001 – £925,000", cap: 675000, rate: 0.05 },
  { label: "£925,001 – £1,500,000", cap: 575000, rate: 0.10 },
  { label: "Over £1,500,000", cap: Infinity, rate: 0.12 },
]
const SDLT_FTB: Band[] = [
  { label: "£0 – £425,000", cap: 425000, rate: 0 },
  { label: "£425,001 – £625,000", cap: 200000, rate: 0.05 },
]
const LBTT_BANDS: Band[] = [
  { label: "£0 – £145,000", cap: 145000, rate: 0 },
  { label: "£145,001 – £250,000", cap: 105000, rate: 0.02 },
  { label: "£250,001 – £325,000", cap: 75000, rate: 0.05 },
  { label: "£325,001 – £750,000", cap: 425000, rate: 0.10 },
  { label: "Over £750,000", cap: Infinity, rate: 0.12 },
]
const LTT_BANDS: Band[] = [
  { label: "£0 – £225,000", cap: 225000, rate: 0 },
  { label: "£225,001 – £400,000", cap: 175000, rate: 0.06 },
  { label: "£400,001 – £750,000", cap: 350000, rate: 0.075 },
  { label: "£750,001 – £1,500,000", cap: 750000, rate: 0.10 },
  { label: "Over £1,500,000", cap: Infinity, rate: 0.12 },
]

const US_RATES: Record<string, { name: string; rate: (price: number) => number; notes: string }> = {
  CA: { name: "California", rate: () => 0.0011, notes: "Base county rate only. City transfer taxes vary (0.055%–2.25% in San Francisco)." },
  NY: { name: "New York", rate: (p) => p < 500000 ? 0.004 : 0.0065, notes: "State base rate. NYC mansion tax (1%+) applies on residential properties over $1M." },
  FL: { name: "Florida", rate: () => 0.007, notes: "State rate only. County surtaxes may apply." },
  TX: { name: "Texas", rate: () => 0, notes: "No state transfer tax in Texas." },
  IL: { name: "Illinois", rate: () => 0.001, notes: "State rate only. Cook County and Chicago add additional taxes." },
  other: { name: "Other State", rate: () => 0.005, notes: "Estimated average. US transfer taxes vary heavily by county and city." },
}

function calcBanded(price: number, bands: Band[], surcharge: number): { total: number; breakdown: BandResult[] } {
  let remaining = price
  let total = 0
  const breakdown: BandResult[] = []
  for (const b of bands) {
    const taxable = Math.min(remaining, b.cap)
    if (taxable <= 0) break
    const effectiveRate = b.rate + surcharge
    const tax = taxable * effectiveRate
    breakdown.push({ label: b.label, effectiveRate, taxable, tax })
    total += tax
    remaining -= taxable
    if (remaining <= 0) break
  }
  return { total, breakdown }
}

const REGION_LABELS: Record<Region, string> = {
  england: "England",
  scotland: "Scotland",
  wales: "Wales",
  us: "US States",
}

export default function StampDutyCalculatorPage() {
  const { tool } = useOutletContext<{ tool: ToolDef; currency: string }>()

  const [region, setRegion] = useState<Region>("england")
  const [priceStr, setPriceStr] = useState("")
  const [propertyType, setPropertyType] = useState<PropertyType>("main")
  const [firstTimeBuyer, setFirstTimeBuyer] = useState(false)
  const [usState, setUSState] = useState<USState>("NY")

  const results = useMemo(() => {
    const price = parseFloat(priceStr) || 0
    if (price <= 0) return null

    if (region === "england") {
      const isBTL = propertyType === "btl" || propertyType === "company"
      const surcharge = isBTL ? 0.03 : 0
      const isFTB = propertyType === "main" && firstTimeBuyer && price <= 625000
      const bands = isFTB ? SDLT_FTB : SDLT_STANDARD
      const { total, breakdown } = calcBanded(price, bands, surcharge)
      return { total, breakdown, sym: "£", taxName: "SDLT", fullName: "Stamp Duty Land Tax", legalFees: 2000 }
    }

    if (region === "scotland") {
      const isBTL = propertyType === "btl" || propertyType === "company"
      const surcharge = isBTL ? 0.06 : 0
      const { total, breakdown } = calcBanded(price, LBTT_BANDS, surcharge)
      return { total, breakdown, sym: "£", taxName: "LBTT", fullName: "Land & Buildings Transaction Tax", legalFees: 2000 }
    }

    if (region === "wales") {
      const isBTL = propertyType === "btl" || propertyType === "company"
      const surcharge = isBTL ? 0.04 : 0
      const { total, breakdown } = calcBanded(price, LTT_BANDS, surcharge)
      return { total, breakdown, sym: "£", taxName: "LTT", fullName: "Land Transaction Tax", legalFees: 2000 }
    }

    // US
    const stateData = US_RATES[usState]
    const rate = stateData.rate(price)
    const total = price * rate
    return {
      total,
      breakdown: [] as BandResult[],
      sym: "$",
      taxName: "Transfer Tax",
      fullName: `${stateData.name} Transfer Tax`,
      legalFees: 3000,
      usNotes: stateData.notes,
      usRate: rate,
    }
  }, [priceStr, region, propertyType, firstTimeBuyer, usState])

  const fmt = (n: number, sym: string) =>
    `${sym}${Math.round(n).toLocaleString()}`

  const price = parseFloat(priceStr) || 0

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6 space-y-5">

          {/* Region Selector */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Select Region</p>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-slate-50 w-full sm:w-auto">
              {(["england", "scotland", "wales", "us"] as Region[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
                    region === r
                      ? "bg-primary text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {REGION_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Inputs */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Property Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Purchase Price ({region === "us" ? "$" : "£"})
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    {region === "us" ? "$" : "£"}
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={priceStr}
                    onChange={(e) => setPriceStr(e.target.value)}
                    placeholder="e.g. 350000"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              {region !== "us" && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Property Type</label>
                    <select
                      value={propertyType}
                      onChange={(e) => setPropertyType(e.target.value as PropertyType)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="main">Main Residence</option>
                      <option value="btl">Buy-to-Let / Second Home</option>
                      <option value="company">Company Purchase</option>
                    </select>
                  </div>

                  {region === "england" && propertyType === "main" && (
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={firstTimeBuyer}
                        onChange={(e) => setFirstTimeBuyer(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/40"
                      />
                      <div>
                        <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">First-Time Buyer</span>
                        <p className="text-xs text-slate-400">Relief applies up to £625,000</p>
                      </div>
                    </label>
                  )}
                </>
              )}

              {region === "us" && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">State</label>
                  <select
                    value={usState}
                    onChange={(e) => setUSState(e.target.value as USState)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="CA">California</option>
                    <option value="NY">New York</option>
                    <option value="FL">Florida</option>
                    <option value="TX">Texas (no transfer tax)</option>
                    <option value="IL">Illinois</option>
                    <option value="other">Other State (estimate)</option>
                  </select>
                  {results?.usNotes && (
                    <p className="text-xs text-slate-500 mt-2 leading-relaxed">{results.usNotes}</p>
                  )}
                </div>
              )}
            </div>

            {/* Results */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results || price <= 0 ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter a purchase price to calculate tax</p>
              ) : (
                <>
                  <div className="text-center py-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{results.fullName}</p>
                    <p className="text-5xl font-extrabold text-primary">{fmt(results.total, results.sym)}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Effective rate: {price > 0 ? ((results.total / price) * 100).toFixed(2) : "0.00"}%
                    </p>
                  </div>

                  {/* UK Band Breakdown Table */}
                  {results.breakdown.length > 0 && (
                    <div className="border-t border-primary/10 pt-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Band Breakdown</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500">
                              <th className="text-left pb-1 font-medium">Band</th>
                              <th className="text-right pb-1 font-medium">Rate</th>
                              <th className="text-right pb-1 font-medium">Taxable</th>
                              <th className="text-right pb-1 font-medium">Tax</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {results.breakdown.map((b, i) => (
                              <tr key={i} className="text-slate-700">
                                <td className="py-1.5 pr-2">{b.label}</td>
                                <td className="text-right py-1.5">{(b.effectiveRate * 100).toFixed(0)}%</td>
                                <td className="text-right py-1.5">{fmt(b.taxable, results.sym)}</td>
                                <td className="text-right py-1.5 font-medium">{fmt(b.tax, results.sym)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-primary/20 font-semibold text-slate-900">
                              <td className="pt-1.5" colSpan={2}>Total</td>
                              <td className="text-right pt-1.5">{fmt(price, results.sym)}</td>
                              <td className="text-right pt-1.5 text-primary">{fmt(results.total, results.sym)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* US simplified rate */}
                  {region === "us" && results.usRate !== undefined && (
                    <div className="border-t border-primary/10 pt-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">State Rate Applied</span>
                        <span className="font-semibold text-slate-900">{(results.usRate * 100).toFixed(3)}%</span>
                      </div>
                    </div>
                  )}

                  {/* Total Purchase Cost */}
                  <div className="border-t border-primary/10 pt-4 bg-white rounded-xl p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Total Purchase Cost</p>
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Purchase Price</span><span>{fmt(price, results.sym)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>{results.taxName}</span><span>{fmt(results.total, results.sym)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Est. Legal Fees</span><span>≈ {fmt(results.legalFees, results.sym)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold text-slate-900 pt-1 border-t border-slate-100 mt-1">
                      <span>Estimated Total</span>
                      <span className="text-primary">{fmt(price + results.total + results.legalFees, results.sym)}</span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-400 italic text-center">
                    Estimate only. Consult a solicitor before exchanging contracts.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Total Tax = Σ (Band Taxable Amount × Effective Rate)\n\nEffective Rate = Base Band Rate + Surcharge (BTL/Second Home)\n\nEngland BTL Surcharge: +3% | Scotland ADS: +6% | Wales: +4%`}
        description="Each region uses a banded (progressive) structure. Only the portion of the purchase price within each band is taxed at that band's rate — not the entire price."
      />

      <ToolFAQ
        items={[
          {
            q: "Do I pay stamp duty on a buy-to-let property?",
            a: "Yes, and you pay a surcharge on top of standard rates. In England, BTL and second homes attract a 3% surcharge on every band. Scotland charges an Additional Dwelling Supplement (ADS) of 6%, and Wales charges 4% higher LTT rates for additional dwellings.",
          },
          {
            q: "What is First-Time Buyer Relief?",
            a: "In England, first-time buyers pay 0% SDLT on the first £425,000 (instead of £250,000) and 5% on the portion between £425,001 and £625,000. For properties over £625,000, standard rates apply with no relief.",
          },
          {
            q: "Is stamp duty the same across the UK?",
            a: "No. Each nation has its own tax: England uses SDLT, Scotland uses LBTT (Land and Buildings Transaction Tax), and Wales uses LTT (Land Transaction Tax). Each has different band thresholds and surcharge rates.",
          },
        ]}
      />

      <CTABox
        icon={Landmark}
        heading="Keep all your property costs in one place"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
